import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { PaginationInstance } from 'ngx-pagination';
import { PlatformService } from 'src/app/shared/services/platform.service';

@Component({
    selector: 'urban-paginator',
    templateUrl: './paginator.component.html',
    styleUrl: './paginator.component.scss',
    standalone: false
})
export class PaginatorComponent implements OnChanges {
  @Input() config: PaginationInstance = {
    itemsPerPage: 10,
    currentPage: 1,
    id: 'default'
  };

  @Input() totalItems: number = 0;
  @Input() pageSizeOptions: number[] = [10, 20, 50, 100];
  @Input() showPageSizeOption: boolean = true;

  @Output() pageChange = new EventEmitter<number>();
  @Output() pageSizeChange = new EventEmitter<number>();
  @Output() configUpdated = new EventEmitter<PaginationInstance>();

  // On a phone the pages grow in place instead of being browsed one by one.
  protected readonly isMobile = inject(PlatformService).isMobileViewport;

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['totalItems'] || changes['config'])) {
      this.checkAndFixPagination();
    }
  }

  private checkAndFixPagination(): void {
    const itemsPerPage = this.config.itemsPerPage;
    const maxPage = Math.max(1, Math.ceil(this.totalItems / itemsPerPage));
    let needsUpdate = false;

    if (this.config.currentPage > maxPage) {
      this.config.currentPage = maxPage;
      needsUpdate = true;
    }

    if (this.totalItems === 0) {
      this.config.currentPage = 1;
      needsUpdate = true;
    }

    if (needsUpdate) {
      this.configUpdated.emit(this.config);
    }
  }

  public onPageChange(pageNumber: number): void {
    this.config.currentPage = pageNumber;
    this.pageChange.emit(pageNumber);
  }

  public onPageSizeChange(pageSize: number): void {
    this.config.itemsPerPage = pageSize;
    this.checkAndFixPagination();
    this.pageSizeChange.emit(pageSize);
  }

  public hasMore(): boolean {
    return this.totalItems > this.config.currentPage * this.config.itemsPerPage;
  }

  public loadMore(): void {
    this.config.currentPage = 1;
    this.onPageSizeChange(this.config.itemsPerPage + this.pageSizeOptions[0]);
  }

  public getMaxPage(): number {
    return Math.max(1, Math.ceil(this.totalItems / this.config.itemsPerPage));
  }

  public getPageCountLabel(): string {
    const maxPage = this.getMaxPage();
    return maxPage > 1 ? 'GENERAL.PAGES' : 'GENERAL.PAGE';
  }
}